/**
 * Consolidated media rail: one hero image with a thumbnail strip and a
 * source switcher (Inventory / Catalog / All Catalog / Sendback /
 * Workstation). Replaces five stacked gallery sections; only sources with
 * images are offered.
 */
import { useMemo, useState } from 'react';
import { Box, Dialog, Stack, Tab, Tabs, Typography } from '@mui/material';
import HideImageOutlinedIcon from '@mui/icons-material/HideImageOutlined';

import { SectionLabel } from './ItemDetailSections';
import type { ImageUrlMap, InventoryItemRecord } from './types';

interface MediaSource {
  key: string;
  label: string;
  images: Array<[string, string]>;
}

const buildSources = (record: InventoryItemRecord): MediaSource[] => {
  // Same blanking rule as the admin list: pending-photography items whose
  // images aren't their own show no inventory images.
  const hideOwnImages = record.state === 'PHOTOGRAPHY_PENDING' && !record.isImageFromInventory;
  const entries = (images: ImageUrlMap | null | undefined): Array<[string, string]> =>
    Object.entries(images ?? {}).filter((entry): entry is [string, string] => !!entry[1]);

  const sources: MediaSource[] = [
    { key: 'inventory', label: 'Inventory', images: hideOwnImages ? [] : entries(record.thumbnailImageUrls ?? record.imageUrls) },
    { key: 'catalog', label: 'Catalog', images: entries(record.catalogImageUrls) },
    { key: 'allCatalog', label: 'All Catalog', images: entries(record.allCatalogImageUrls) },
    ...(record.isSupplierSendback ?
      [{ key: 'sendback', label: 'Sendback', images: entries(record.sendbackThumbnailImageUrls ?? record.sendbackImageUrls) }] :
      []),
    { key: 'workstation', label: 'Workstation', images: entries(record.workstationImageUrls) },
  ];
  return sources.filter(source => source.images.length > 0);
};

export const ItemMediaGallery = ({ record }: { record: InventoryItemRecord }) => {
  const sources = useMemo(() => buildSources(record), [record]);
  const [sourceIndex, setSourceIndex] = useState(0);
  const [positionIndex, setPositionIndex] = useState(0);
  const [zoomed, setZoomed] = useState(false);

  const source = sources[Math.min(sourceIndex, sources.length - 1)];
  const current = source?.images[Math.min(positionIndex, (source?.images.length ?? 1) - 1)];

  if (!sources.length) {
    return (
      <Box>
        <SectionLabel>Photos</SectionLabel>
        <Stack
          alignItems="center"
          justifyContent="center"
          spacing={1}
          sx={{
            width: 220,
            height: 220,
            borderRadius: 1,
            border: '1px dashed',
            borderColor: 'divider',
            color: 'text.disabled',
            bgcolor: 'grey.50',
          }}
        >
          <HideImageOutlinedIcon />
          <Typography variant="caption">No photos yet</Typography>
        </Stack>
      </Box>
    );
  }

  return (
    <Box sx={{ width: 236 }}>
      <SectionLabel>Photos</SectionLabel>
      <Box
        component="img"
        src={current?.[1]}
        alt={`${source.label} — ${current?.[0]}`}
        onClick={() => setZoomed(true)}
        sx={{
          width: 220,
          height: 220,
          objectFit: 'cover',
          borderRadius: 1,
          border: '1px solid',
          borderColor: 'divider',
          cursor: 'zoom-in',
          display: 'block',
        }}
      />
      <Stack direction="row" spacing={0.75} useFlexGap sx={{ flexWrap: 'wrap', mt: 1 }}>
        {source.images.map(([position, url], index) => (
          <Box key={position} sx={{ textAlign: 'center' }}>
            <Box
              component="img"
              src={url}
              alt={position}
              onClick={() => setPositionIndex(index)}
              sx={{
                width: 44,
                height: 44,
                objectFit: 'cover',
                borderRadius: 0.75,
                cursor: 'pointer',
                display: 'block',
                border: '2px solid',
                borderColor: index === positionIndex ? 'primary.main' : 'divider',
              }}
            />
            <Typography sx={{ fontSize: 10, color: 'text.secondary' }}>{position}</Typography>
          </Box>
        ))}
      </Stack>
      {sources.length > 1 && (
        <Tabs
          value={sourceIndex}
          onChange={(_event, value: number) => { setSourceIndex(value); setPositionIndex(0); }}
          variant="scrollable"
          scrollButtons={false}
          sx={{
            minHeight: 30,
            mt: 0.5,
            '& .MuiTab-root': { minHeight: 30, minWidth: 0, px: 1, py: 0.5, fontSize: 11.5 },
          }}
        >
          {sources.map(({ key, label, images }) => (
            <Tab key={key} label={`${label} (${images.length})`} />
          ))}
        </Tabs>
      )}
      <Dialog open={zoomed} onClose={() => setZoomed(false)} maxWidth="md">
        {current && (
          <Box component="img" src={current[1]} alt="Zoomed item" sx={{ maxWidth: '100%', maxHeight: '80vh' }} />
        )}
      </Dialog>
    </Box>
  );
};

export default ItemMediaGallery;
